import { useAppSelector, useAppDispatch } from "@/lib/hooks"
import { setSplitPayments } from "@/redux/slices/orderFormSlice"
import usePaymentMethods from "@/hooks/usePaymentMethods"
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  IconButton,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material"
import { Trash2 } from "lucide-react"
import { useState } from "react"

interface PaymentMethod {
  id: number
  display_name: string
}

interface SelectedPaymentMethod extends PaymentMethod {
  amount: number
}

interface SplitBillingProps {
  onChange?: (selected: SelectedPaymentMethod[]) => void
}

export default function SplitBilling({ onChange }: SplitBillingProps) {
  const dispatch = useAppDispatch()
  const orderState = useAppSelector((state) => state.order)
  const { paymentMethods, isLoading: paymentLoading } = usePaymentMethods()

  const [selectedId, setSelectedId] = useState("")

  // Update Redux when split payments change
  const updateSplitPayments = (payments: SelectedPaymentMethod[]) => {
    dispatch(setSplitPayments(payments))
    onChange?.(payments)
  }

  const handleSelect = (event: SelectChangeEvent) => {
    const id = Number(event.target.value)
    setSelectedId("") // Reset selection

    const paymentMethodsList = (paymentMethods as any)?.data || paymentMethods
    const selectedMethod = (paymentMethodsList as PaymentMethod[])?.find(
      (pm: PaymentMethod) => pm.id === id
    )
    if (!selectedMethod) return

    // prevent duplicates
    const currentPayments = orderState.splitPayments || []
    const exists = currentPayments.some((m) => m.id === id)
    if (exists) return

    const newPayments = [...currentPayments, { ...selectedMethod, amount: 0 }]
    updateSplitPayments(newPayments)
  }

  const handleAmountChange = (id: number, amount: number) => {
    const currentPayments = orderState.splitPayments || []
    const newPayments = currentPayments.map((pm) =>
      pm.id === id ? { ...pm, amount: isNaN(amount) ? 0 : amount } : pm
    )
    updateSplitPayments(newPayments)
  }

  const handleDelete = (id: number) => {
    const currentPayments = orderState.splitPayments || []
    const newPayments = currentPayments.filter((pm) => pm.id !== id)
    updateSplitPayments(newPayments)
  }

  return (
    <div className="space-y-4">
      {/* Select Payment Method */}
      {paymentLoading ? (
        <div className="flex justify-center py-4">
          <CircularProgress size={24} />
        </div>
      ) : (
        <FormControl fullWidth>
          <InputLabel id="payment-method-label">
            Select Payment Method
          </InputLabel>
          <Select
            labelId="payment-method-label"
            id="payment-method-select"
            value={selectedId}
            label="Select Payment Method"
            onChange={handleSelect}
          >
            {((paymentMethods as any)?.data || paymentMethods)?.map(
              (pm: PaymentMethod) => (
                <MenuItem key={pm.id} value={pm.id}>
                  {pm.display_name}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
      )}

      {/* Selected Payment Methods */}
      <div className="space-y-3">
        {orderState.splitPayments?.map((pm) => (
          <div
            key={pm.id}
            className="flex items-center gap-3 justify-between rounded-lg px-3"
          >
            <div className="flex-1 font-medium">{pm.display_name}</div>

            <div className="w-[120px]">
              <TextField
                label="Amount"
                type="number"
                size="small"
                value={pm.amount}
                onFocus={(event) => {
                  event.target.select()
                }}
                onChange={(e) =>
                  handleAmountChange(pm.id, Number(e.target.value))
                }
                fullWidth
              />
            </div>

            <IconButton
              aria-label="delete"
              onClick={() => handleDelete(pm.id)}
              color="error"
              size="small"
            >
              <Trash2 size={18} />
            </IconButton>
          </div>
        ))}
      </div>
    </div>
  )
}
